import React from 'react';
import { Button, ConfirmButton, CustomTooltip, PromptType } from '@hwm/ui-lib';
import { t, Trans } from '@lingui/macro';
import { useAppSelector } from '@core/store/store';
import { selectedAccStateSelector } from '@core/store/library-stores';
import { usePmZoneSiteListDataSource, useSetAccDataSource } from '@features/dataSource';
import { useRemovePMZDataSource } from './removePMZDataSource';
import { SiderKeys, useSiderAnimation } from '../../Sidebar.utils';
import { useLoading } from '@shared/hooks/useLoading';
import { useSidebar } from '@shared/hooks/useSider';
import { Typography } from '@material-ui/core';

interface RemovePMZButtonProps {
  disabled: boolean;
  parentId: string;
}

export const RemovePMZButton: React.FC<RemovePMZButtonProps> = ({ disabled, parentId }) => {
  const { pmZoneData } = useAppSelector(selectedAccStateSelector);
  const { pmZoneSites } = usePmZoneSiteListDataSource();

  const { handleSidebarSection } = useSidebar();
  const { showData } = useSiderAnimation(false);

  const { setAccData } = useSetAccDataSource();
  const { removePMZone, loading } = useRemovePMZDataSource();

  const [loadingState] = useLoading(loading, 3000);

  return (
    <ConfirmButton
      promptProps={{
        content: (
          <Typography>
            <Trans>
              Please note that removing "{pmZoneData?.name}" PMZone will delete any data associated to this PMZone.
            </Trans>
          </Typography>
        ),
        title: t`Remove PMZone`,
        type: PromptType.Warning,
        onConfirm: () => {
          removePMZone(pmZoneData?.accountId, () => {
            setTimeout(() => {
              setAccData({ accountId: parentId }).then(() => {
                handleSidebarSection(SiderKeys.accHierarchy);
              });
            }, 2000);
          });
        },
        confirmLabel: t`Remove`,
      }}
      dataTestId="sidebar-header-buttons-remove-pmz-confirm-prompt"
    >
      {({ onClick }) => (
        <div className="flex items-center mx-2">
          <Button
            style={{
              padding: 12,
            }}
            variant="text"
            onClick={onClick}
            loading={loadingState}
            disabled={disabled || pmZoneSites?.length > 0 || loadingState || !showData}
            dataTestId="sidebar-header-buttons-remove-pmz-confirm-prompt-button"
          >
            <Trans id="[Action.RemovePMZone]">Remove PMZ</Trans>
          </Button>
          {(disabled || pmZoneSites?.length > 0) && (
            <CustomTooltip
              tooltipText={t`PMZone can be deleted only when there are no associated sites to it.`}
              dataTestId="sidebar-header-buttons-remove-pmz-confirm-prompt-tooltip"
            />
          )}
        </div>
      )}
    </ConfirmButton>
  );
};
